import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Title as ChartTitle,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
// import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo, useState } from "react";
import { Doughnut, Line } from "react-chartjs-2";
import {
  Activity,
  AlertCircle,
  Calendar,
  ChartDots,
  ChartLine,
  ChartPie,
  School,
  TrendingUp,
  Users,
} from "tabler-icons-react";
import {
  useGetAllElevesQuery,
  useGetStudentActivityQuery,
} from "../../redux/features/eleveApi";
import {
  useGetParentsQuery,
  useGetProspectsQuery,
  useGetProspectsStatsQuery,
} from "../../redux/features/parentApi";
import AppUsageByHourChart from "./components/AppUsageByHourChart";
import AppUsageChart, {
  UsageMetricCard,
  useAppUsageData,
} from "./components/AppUsageChart";
import ParentEngagementChart from "./components/ParentEngagementChart";
import StudentActivityChart from "./components/StudentActivityChart";

// Register chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  // ChartDataLabels,
);

// Register dayjs plugins
dayjs.extend(isBetween);
dayjs.locale("fr");

const FUNNEL_CONFIG = [
  {
    key: "CONTACT",
    label: "Contacts",
    shortLabel: "Contact",
    color: "blue",
    hex: "#3b82f6",
  },
  {
    key: "PROSPECT",
    label: "Prospects",
    shortLabel: "Prospect",
    color: "yellow",
    hex: "#f59e0b",
  },
  {
    key: "USER",
    label: "Utilisateurs",
    shortLabel: "Utilisateur",
    color: "teal",
    hex: "#14b8a6",
  },
  {
    key: "USER_ACTIVE",
    label: "Utilisateurs activés",
    shortLabel: "Actif",
    color: "green",
    hex: "#10b981",
  },
  {
    key: "CLIENT",
    label: "Clients",
    shortLabel: "Client",
    color: "red",
    hex: "#ef4444",
  },
  {
    key: "CLIENT_ACTIF",
    label: "Clients actifs",
    shortLabel: "Actif 30j",
    color: "orange",
    hex: "#f97316",
  },
  {
    key: "CLIENT_ENGAGE",
    label: "Clients engagés",
    shortLabel: "Engagé 40j",
    color: "grape",
    hex: "#a855f7",
  },
  {
    key: "CLIENT_IMPACTE",
    label: "Clients impactés 40J",
    shortLabel: "Impacté",
    color: "violet",
    hex: "#7c3aed",
  },
];

const StatsSummaryCard = ({
  title,
  value,
  icon,
  color,
  subtext,
  switchProps,
}) => (
  <Card shadow="sm" p="md" radius="md" withBorder>
    <Group position="apart" mb="sm">
      <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
        {title}
      </Text>
      <ThemeIcon color={color} size={42} radius="md" variant="light">
        {icon}
      </ThemeIcon>
    </Group>
    <Text weight={700} size="xl" mb="xs">
      {value}
    </Text>
    {subtext && (
      <Text size="xs" color="dimmed" mb={switchProps ? "xs" : 0}>
        {subtext}
      </Text>
    )}
    {switchProps && (
      <Switch
        label={switchProps.label}
        checked={switchProps.checked}
        onChange={switchProps.onChange}
        size="sm"
        color={color}
        styles={{
          label: { fontSize: "11px", fontWeight: 500 },
        }}
      />
    )}
  </Card>
);

const StatistiquesPage = () => {
  const [userFilter, setUserFilter] = useState("all"); // 'all', 'students', 'parents'
  const [periodFilter, setPeriodFilter] = useState("month");
  const [customDates, setCustomDates] = useState([null, null]);
  const [includeStudentsInActiveUsers, setIncludeStudentsInActiveUsers] =
    useState(true);
  const [provenanceFilter, setProvenanceFilter] = useState("all"); // 'all', 'Genimi', 'Senassurance'
  const [funnelByPeriod, setFunnelByPeriod] = useState(false); // false = all-time, true = période sélectionnée

  // Fetch with limit parameter (10000) to ensure we get all records
  // This was critical for getting all parents in previous debugging
  const {
    data: elevesData,
    isLoading: isLoadingEleves,
    error: errorEleves,
  } = useGetAllElevesQuery({ limit: 50000 });

  const {
    data: parentsData,
    isLoading: isLoadingParents,
    error: errorParents,
  } = useGetParentsQuery({ limit: 50000 });

  // Liste prospects/contacts pour le funnel par période (source contenant computed_category)
  const {
    data: prospectsListData,
    isLoading: isLoadingProspectsList,
    error: errorProspectsList,
  } = useGetProspectsQuery({ page: 1, limit: 10000 });

  // Source de vérité pour les compteurs du funnel (même endpoint que ProspectIndexPage)
  const { data: prospectsStatsData, isLoading: isLoadingFunnelStats } =
    useGetProspectsStatsQuery(undefined, { refetchOnMountOrArgChange: true });

  const isLoading = isLoadingEleves || isLoadingParents;
  const error = errorEleves || errorParents;

  const dateRange = useMemo(() => {
    const now = dayjs();
    let start = now.startOf("month");
    let end = now.endOf("month");

    switch (periodFilter) {
      case "today":
        start = now.startOf("day");
        end = now.endOf("day");
        break;
      case "yesterday":
        start = now.subtract(1, "day").startOf("day");
        end = now.subtract(1, "day").endOf("day");
        break;
      case "last-week":
        start = now.subtract(1, "week").startOf("week");
        end = now.subtract(1, "week").endOf("week");
        break;
      case "last-month":
        start = now.subtract(1, "month").startOf("month");
        end = now.subtract(1, "month").endOf("month");
        break;
      case "week":
        start = now.startOf("week");
        end = now.endOf("week");
        break;
      case "month":
        start = now.startOf("month");
        end = now.endOf("month");
        break;
      case "year":
        start = now.startOf("year");
        end = now.endOf("year");
        break;
      case "custom":
        if (customDates[0] && customDates[1]) {
          start = dayjs(customDates[0]);
          end = dayjs(customDates[1]).endOf("day");
        } else {
          // Default to month if custom not selected
          start = now.startOf("month");
          end = now.endOf("month");
        }
        break;
      case "all":
        start = dayjs("2020-01-01"); // Sufficiently past date
        end = now.endOf("day");
        break;
      default:
        break;
    }
    return { start, end };
  }, [periodFilter, customDates]);

  // Activité des élèves (diagnostics) sur la période sélectionnée
  const { data: studentActivityData, isLoading: isLoadingStudentActivity } =
    useGetStudentActivityQuery(
      {
        startDate:
          periodFilter === "custom" && (!customDates[0] || !customDates[1])
            ? undefined
            : dateRange.start.format("YYYY-MM-DD"),
        endDate:
          periodFilter === "custom" && (!customDates[0] || !customDates[1])
            ? undefined
            : dateRange.end.format("YYYY-MM-DD"),
      },
      { refetchOnMountOrArgChange: true },
    );

  // Core Data Processing for Chart
  const processedData = useMemo(() => {
    const { start, end } = dateRange;
    const labels = [];
    const dataMap = new Map();

    // Generate labels and initialize map based on period
    let current = start.clone();

    // Logic to group data
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    if (isYearOrAll) {
      // Group by Month
      let currMonth = start.clone().startOf("month");
      while (currMonth.isBefore(end) || currMonth.isSame(end, "month")) {
        const label = currMonth.format("MMM YYYY");
        labels.push(label);
        dataMap.set(label, 0);
        currMonth = currMonth.add(1, "month");
      }
    } else {
      // Group by Day
      while (current.isBefore(end) || current.isSame(end, "day")) {
        const label = current.format("DD MMM");
        labels.push(label);
        dataMap.set(label, 0);
        current = current.add(1, "day");
      }
    }

    // Helper to process a list of items
    const processItems = (items, isParent = false) => {
      if (!items) return 0;
      let count = 0;
      items.forEach((item) => {
        // Filtrer par provenance si c'est un parent
        if (isParent && provenanceFilter !== "all") {
          if (
            !item.provenance ||
            item.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return;
          }
        }

        // Safe date access with fallback for consistency
        const dateStr =
          item.createdAt ||
          item.created_at ||
          item.date_inscription ||
          item?.parent?.created_at;

        if (!dateStr) return;

        const date = dayjs(dateStr);
        if (!date.isValid()) return;

        if (date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")) {
          const label = isYearOrAll
            ? date.format("MMM YYYY")
            : date.format("DD MMM");

          if (dataMap.has(label)) {
            dataMap.set(label, dataMap.get(label) + 1);
          }
          count++;
        }
      });
      return count;
    };

    let totalFiltered = 0;

    // Apply User Filter for Chart Data
    // Note: We might want separate totals for the summary cards
    // but the chart itself follows the userFilter state.
    if (userFilter === "all" || userFilter === "students") {
      totalFiltered += processItems(elevesData?.data, false);
    }
    if (userFilter === "all" || userFilter === "parents") {
      totalFiltered += processItems(parentsData?.data, true);
    }

    return {
      labels,
      chartValues: labels.map((l) => dataMap.get(l)),
      totalFiltered,
    };
  }, [
    userFilter,
    dateRange,
    elevesData,
    parentsData,
    periodFilter,
    provenanceFilter,
  ]);

  // Calculate Summary Stats regardless of userFilter for the top cards
  const summaryStats = useMemo(() => {
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    const countInPeriod = (items, isParent = false) => {
      if (!items) return 0;
      return items.filter((item) => {
        // Filtrer par provenance si c'est un parent
        if (isParent && provenanceFilter !== "all") {
          if (
            !item.provenance ||
            item.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return false;
          }
        }

        const dateStr =
          item.createdAt ||
          item.created_at ||
          item.date_inscription ||
          item?.parent?.created_at;
        if (!dateStr) return false;
        const date = dayjs(dateStr);
        return (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        );
      }).length;
    };

    return {
      students: countInPeriod(elevesData?.data, false),
      parents: countInPeriod(parentsData?.data, true),
    };
  }, [elevesData, parentsData, dateRange, periodFilter, provenanceFilter]);

  // Data processing for Doughnut Chart (Levels)
  const levelByChartData = useMemo(() => {
    if (!elevesData?.data) return { labels: [], datasets: [] };
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    const levelCounts = {};

    elevesData.data.forEach((eleve) => {
      const dateStr =
        eleve.createdAt || eleve.created_at || eleve.date_inscription;
      if (!dateStr) return;

      const date = dayjs(dateStr);
      if (!date.isValid()) return;

      if (!date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")) {
        return;
      }

      // Assuming eleve.classe.name holds the grade/level info (e.g., "CM2", "CI")
      // Check for 'classe' object or maybe 'niveaux'
      const levelName =
        eleve.classe?.name || eleve.classe?.sub_title || "Non classé";
      levelCounts[levelName] = (levelCounts[levelName] || 0) + 1;
    });

    const labels = Object.keys(levelCounts);
    const data = Object.values(levelCounts);

    // Sort by count descending for better visual
    const sortedIndices = data
      .map((_, i) => i)
      .sort((a, b) => data[b] - data[a]);
    const sortedLabels = sortedIndices.map((i) => labels[i]);
    const sortedData = sortedIndices.map((i) => data[i]);

    // Palette de couleurs étendue pour supporter tous les niveaux scolaires
    const colorPalette = [
      "#3b82f6", // Blue-500
      "#10b981", // Emerald-500
      "#f59e0b", // Amber-500
      "#ef4444", // Red-500
      "#8b5cf6", // Violet-500
      "#ec4899", // Pink-500
      "#06b6d4", // Cyan-500
      "#6366f1", // Indigo-500
      "#84cc16", // Lime-500
      "#14b8a6", // Teal-500
      "#f97316", // Orange-500
      "#db2777", // Pink-600
      "#7c3aed", // Violet-600
      "#0ea5e9", // Sky-500
      "#22c55e", // Green-500
      "#eab308", // Yellow-500
      "#a855f7", // Purple-500
      "#14b8a6", // Teal-500
      "#f43f5e", // Rose-500
      "#06b6d4", // Cyan-500
    ];

    // Générer des couleurs supplémentaires si nécessaire
    const backgroundColor = sortedLabels.map((_, index) => {
      if (index < colorPalette.length) {
        return colorPalette[index];
      }
      // Générer une couleur aléatoire pour les niveaux supplémentaires
      const hue = (index * 137.508) % 360; // Golden angle pour distribution uniforme
      return `hsl(${hue}, 65%, 55%)`;
    });

    return {
      labels: sortedLabels,
      datasets: [
        {
          data: sortedData,
          backgroundColor,
          borderColor: "#ffffff",
          borderWidth: 2,
        },
      ],
    };
  }, [elevesData, dateRange, periodFilter]);

  // Calculate total app usage for summary card
  const totalAppUsage = useMemo(() => {
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    if (!parentsData?.data) return 0;

    return parentsData.data.filter((parent) => {
      // Filtrer par provenance
      if (provenanceFilter !== "all") {
        if (
          !parent.provenance ||
          parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
        ) {
          return false;
        }
      }

      const usageDate = parent.last_credit_usage;
      if (!usageDate) return false;

      const date = dayjs(usageDate);
      if (!date.isValid()) return false;

      return date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]");
    }).length;
  }, [dateRange, parentsData, periodFilter, provenanceFilter]);

  // Calculate total active users (new registrations + credit usage + connections without credit)
  const totalActiveUsers = useMemo(() => {
    const { start, end } = dateRange;
    const isYearOrAll = periodFilter === "year" || periodFilter === "all";

    if (!elevesData?.data && !parentsData?.data) return 0;

    // Set pour éviter les doublons
    const activeUserIds = new Set();

    // 1. Nouveaux inscrits (élèves + parents) dans la période
    if (includeStudentsInActiveUsers && elevesData?.data) {
      elevesData.data.forEach((eleve) => {
        const dateStr =
          eleve.createdAt || eleve.created_at || eleve.date_inscription;
        if (!dateStr) return;

        const date = dayjs(dateStr);
        if (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        ) {
          activeUserIds.add(`eleve-${eleve.id}`);
        }
      });
    }

    if (parentsData?.data) {
      parentsData.data.forEach((parent) => {
        // Filtrer par provenance
        if (provenanceFilter !== "all") {
          if (
            !parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return;
          }
        }

        const dateStr =
          parent.createdAt || parent.created_at || parent.date_inscription;
        if (!dateStr) return;

        const date = dayjs(dateStr);
        if (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        ) {
          activeUserIds.add(`parent-${parent.id}`);
        }
      });

      // 2. Parents qui ont utilisé du crédit dans la période
      parentsData.data.forEach((parent) => {
        // Filtrer par provenance
        if (provenanceFilter !== "all") {
          if (
            !parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return;
          }
        }

        const creditDate = parent.last_credit_usage;
        if (!creditDate) return;

        const date = dayjs(creditDate);
        if (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        ) {
          activeUserIds.add(`parent-${parent.id}`);
        }
      });

      // 3. Parents qui se sont connectés dans la période (sans nécessairement utiliser du crédit)
      parentsData.data.forEach((parent) => {
        // Filtrer par provenance
        if (provenanceFilter !== "all") {
          if (
            !parent.provenance ||
            parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
          ) {
            return;
          }
        }

        const loginDate = parent.last_login;
        if (!loginDate) return;

        const date = dayjs(loginDate);
        if (
          date.isValid() &&
          date.isBetween(start, end, isYearOrAll ? "month" : "day", "[]")
        ) {
          activeUserIds.add(`parent-${parent.id}`);
        }
      });
    }

    return activeUserIds.size;
  }, [
    dateRange,
    parentsData,
    elevesData,
    periodFilter,
    includeStudentsInActiveUsers,
    provenanceFilter,
  ]);

  // Funnel counts ALL-TIME : source = endpoint backend dédié
  const funnelCounts = useMemo(() => {
    const d = prospectsStatsData?.data ?? {};
    return FUNNEL_CONFIG.reduce((acc, { key }) => {
      acc[key] = d[key] ?? 0;
      return acc;
    }, {});
  }, [prospectsStatsData]);

  // Funnel counts PÉRIODE : calculé depuis la liste prospects filtrée sur la période
  // Un parent est pris en compte uniquement s'il est inscrit dans la fenêtre temporelle sélectionnée
  const funnelCountsByPeriod = useMemo(() => {
    const counts = FUNNEL_CONFIG.reduce((acc, { key }) => {
      acc[key] = 0;
      return acc;
    }, {});
    const prospects = Array.isArray(prospectsListData?.data)
      ? prospectsListData.data
      : Array.isArray(prospectsListData)
        ? prospectsListData
        : [];
    if (!Array.isArray(prospects) || prospects.length === 0) return counts;

    const { start, end } = dateRange;
    const grain =
      periodFilter === "year" || periodFilter === "all" ? "month" : "day";

    prospects.forEach((parent) => {
      if (provenanceFilter !== "all") {
        if (
          !parent.provenance ||
          parent.provenance.toLowerCase() !== provenanceFilter.toLowerCase()
        )
          return;
      }
      const registrationDate =
        parent.created_at || parent.createdAt || parent.date_inscription;
      if (!registrationDate) return;

      const registeredAt = dayjs(registrationDate);
      const isRegisteredInPeriod =
        registeredAt.isValid() &&
        registeredAt.isBetween(start, end, grain, "[]");

      if (!isRegisteredInPeriod) return;

      const category = parent.computed_category || parent.type;
      if (category && counts[category] !== undefined) {
        counts[category]++;
      }
    });

    return counts;
  }, [prospectsListData, dateRange, periodFilter, provenanceFilter]);

  // Source active selon le toggle
  const activeFunnelCounts = funnelByPeriod
    ? funnelCountsByPeriod
    : funnelCounts;

  // Comptes CUMULATIFS : chaque étape inclut les parents à cette étape OU plus avancés
  const funnelCumulative = useMemo(() => {
    const cum = {};
    for (let i = 0; i < FUNNEL_CONFIG.length; i++) {
      cum[FUNNEL_CONFIG[i].key] = FUNNEL_CONFIG.slice(i).reduce(
        (sum, { key }) => sum + (activeFunnelCounts[key] ?? 0),
        0,
      );
    }
    return cum;
  }, [activeFunnelCounts]);

  const chartData = {
    labels: processedData.labels,
    datasets: [
      {
        fill: true,
        label: "Inscriptions",
        data: processedData.chartValues,
        borderColor: "#3b82f6",
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(59, 130, 246, 0.5)");
          gradient.addColorStop(1, "rgba(59, 130, 246, 0.0)");
          return gradient;
        },
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#3b82f6",
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 13 },
        displayColors: false,
        callbacks: {
          label: (context) => `${context.parsed.y} Inscriptions`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { maxTicksLimit: 10, color: "#9ca3af" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f3f4f6", borderDash: [5, 5], drawBorder: false },
        ticks: { stepSize: 1, color: "#9ca3af" },
      },
    },
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "right",
        labels: {
          usePointStyle: true,
          padding: 20,
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const dataset = data.datasets[0];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              return data.labels.map((label, i) => {
                const value = dataset.data[i];
                const percentage = ((value / total) * 100).toFixed(1);
                return {
                  text: `${label}: ${percentage}%`,
                  fillStyle: dataset.backgroundColor[i],
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.parsed;
            const total = context.dataset.data.reduce(
              (acc, val) => acc + val,
              0,
            );
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} élèves (${percentage}%)`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <Box p="xl" bg="#f8f9fa" mih="100vh">
      {/* Header Section */}
      <Paper shadow="xs" p="md" mb="xl" radius="md" withBorder>
        <Flex justify="space-between" align="center" wrap="wrap" gap="md">
          <Box>
            <Group spacing="xs" mb={4}>
              <ThemeIcon variant="light" color="blue" size="lg">
                <ChartDots size={20} />
              </ThemeIcon>
              <Title order={2}>Tableau de bord statistiques</Title>
            </Group>
            <Text size="sm" color="dimmed">
              Vue d'ensemble des inscriptions et de l'utilisation de la
              plateforme
            </Text>
          </Box>

          {/* Global Filters */}
          <Flex gap="sm" wrap="wrap" align="center">
            <Select
              placeholder="Période"
              value={periodFilter}
              onChange={setPeriodFilter}
              data={[
                { value: "today", label: "Aujourd'hui" },
                { value: "yesterday", label: "Hier" },
                { value: "last-week", label: "Semaine dernière" },
                { value: "last-month", label: "Mois dernier" },
                { value: "week", label: "Cette semaine" },
                { value: "month", label: "Ce mois" },
                { value: "year", label: "Cette année" },
                { value: "all", label: "Tout" },
                { value: "custom", label: "Personnalisé" },
              ]}
              icon={<Calendar size={16} />}
              size="sm"
              w={160}
              styles={{ input: { fontWeight: 500 } }}
            />

            {periodFilter === "custom" && (
              <DatePickerInput
                type="range"
                placeholder="Sélectionner une période"
                value={customDates}
                onChange={setCustomDates}
                size="sm"
                locale="fr"
                w={240}
              />
            )}

            <Select
              placeholder="Provenance"
              value={provenanceFilter}
              onChange={setProvenanceFilter}
              data={[
                { value: "all", label: "Toutes les provenances" },
                { value: "Genimi", label: "Genimi" },
                { value: "Senassurance", label: "Senassurance" },
              ]}
              icon={<Users size={16} />}
              size="sm"
              w={200}
              styles={{ input: { fontWeight: 500 } }}
            />
          </Flex>
        </Flex>
      </Paper>

      {error && (
        <Alert
          icon={<AlertCircle size={16} />}
          title="Erreur"
          color="red"
          mb="md"
          variant="filled"
        >
          <Text color="white">
            Une erreur est survenue lors du chargement des données.
          </Text>
        </Alert>
      )}

      {/* Summary Cards Section */}
      {/* Première ligne: Inscriptions */}
      <SimpleGrid
        cols={3}
        spacing="lg"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
        mb="lg"
      >
        <StatsSummaryCard
          title="Total Inscriptions"
          value={summaryStats.students + summaryStats.parents}
          subtext="Sur la période sélectionnée"
          icon={<TrendingUp size={24} />}
          color="blue"
        />
        <StatsSummaryCard
          title="Nouveaux Élèves"
          value={summaryStats.students}
          subtext="Élèves inscrits"
          icon={<School size={24} />}
          color="teal"
        />
        <StatsSummaryCard
          title="Nouveaux Parents"
          value={summaryStats.parents}
          subtext="Parents inscrits"
          icon={<Users size={24} />}
          color="orange"
        />
      </SimpleGrid>

      {/* Deuxième ligne: Utilisateurs Actifs et Utilisation App */}
      <SimpleGrid
        cols={2}
        spacing="lg"
        breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        mb="xl"
      >
        <StatsSummaryCard
          title="Comptes actifs"
          value={totalActiveUsers}
          subtext="Inscrits + Connexions + Crédits"
          icon={<ChartLine size={24} />}
          color="grape"
          switchProps={{
            label: "Inclure les nouveaux élèves",
            checked: includeStudentsInActiveUsers,
            onChange: (e) =>
              setIncludeStudentsInActiveUsers(e.currentTarget.checked),
          }}
        />
        <StatsSummaryCard
          title="Utilisations de l'app"
          value={totalAppUsage}
          subtext="Parents ayant utilisé des crédits"
          icon={<Activity size={24} />}
          color="violet"
        />
      </SimpleGrid>

      {/* Section: Inscriptions */}
      <Stack spacing="xl">
        <Box>
          <Group spacing="xs" mb="md">
            <ThemeIcon variant="light" color="blue" size="md">
              <TrendingUp size={18} />
            </ThemeIcon>
            <Title order={3}>Évolution des inscriptions</Title>
          </Group>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <div className="d-flex flex-column flex-xl-row justify-content-between align-items-center mb-4 gap-3">
              <Group spacing="sm">
                <Text size="lg" weight={700}>
                  Tendance temporelle
                </Text>
                <Badge variant="light" color="blue" size="lg">
                  {processedData.totalFiltered} affichés
                </Badge>
              </Group>

              {/* Filter Controls */}
              <Group spacing={4} bg="gray.1" p={4} sx={{ borderRadius: 8 }}>
                <Button
                  size="xs"
                  compact
                  variant={userFilter === "all" ? "filled" : "subtle"}
                  onClick={() => setUserFilter("all")}
                  color={userFilter === "all" ? "blue" : "gray"}
                  disabled={isLoading}
                >
                  Tous
                </Button>
                <Button
                  size="xs"
                  compact
                  variant={userFilter === "students" ? "filled" : "subtle"}
                  onClick={() => setUserFilter("students")}
                  color={userFilter === "students" ? "blue" : "gray"}
                  disabled={isLoading}
                >
                  Élèves
                </Button>
                <Button
                  size="xs"
                  compact
                  variant={userFilter === "parents" ? "filled" : "subtle"}
                  onClick={() => setUserFilter("parents")}
                  color={userFilter === "parents" ? "blue" : "gray"}
                  disabled={isLoading}
                >
                  Parents
                </Button>
              </Group>
            </div>

            <div style={{ height: "350px" }}>
              {isLoading ? (
                <Box
                  h="100%"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Loader size="lg" />
                </Box>
              ) : (
                <Line options={options} data={chartData} />
              )}
            </div>

            <Text
              align="center"
              color="dimmed"
              size="xs"
              mt="md"
              transform="uppercase"
              weight={600}
              letterSpacing={0.5}
            >
              Inscriptions d'élèves et de parents au fil du temps
            </Text>
          </Card>
        </Box>

        <Divider my="xl" />

        {/* Section: Utilisation de l'application */}
        <Box>
          <Group spacing="xs" mb="md">
            <ThemeIcon variant="light" color="violet" size="md">
              <Activity size={18} />
            </ThemeIcon>
            <Title order={3}>Utilisation de l'application</Title>
          </Group>
          <Text size="sm" color="dimmed" mb="lg">
            Analyse de l'engagement des parents avec la plateforme
          </Text>

          {/* Cartes de métriques - toutes en haut */}
          <SimpleGrid
            cols={4}
            spacing="md"
            breakpoints={[
              { maxWidth: "lg", cols: 2 },
              { maxWidth: "sm", cols: 1 },
            ]}
            mb="xl"
          >
            {/* Métriques d'utilisation quotidienne */}
            <UsageMetricCard
              title="Total utilisations"
              value={
                useAppUsageData(
                  parentsData,
                  dateRange,
                  periodFilter,
                  provenanceFilter,
                ).totalUsage
              }
              subtitle="Sur la période"
              icon={<Activity size={20} />}
              color="blue"
            />
            <UsageMetricCard
              title="Parents actifs"
              value={
                useAppUsageData(
                  parentsData,
                  dateRange,
                  periodFilter,
                  provenanceFilter,
                ).activeUsers
              }
              subtitle="Parents ayant utilisé des crédits"
              icon={<Users size={20} />}
              color="green"
            />
            <UsageMetricCard
              title="Moyenne / jour"
              value={
                useAppUsageData(
                  parentsData,
                  dateRange,
                  periodFilter,
                  provenanceFilter,
                ).avgUsagePerDay
              }
              subtitle="Utilisations par jour"
              icon={<ChartLine size={20} />}
              color="cyan"
            />
            <UsageMetricCard
              title="Pic d'utilisation"
              value={
                useAppUsageData(
                  parentsData,
                  dateRange,
                  periodFilter,
                  provenanceFilter,
                ).maxUsageInDay
              }
              subtitle="Maximum en un jour"
              icon={<TrendingUp size={20} />}
              color="orange"
            />
          </SimpleGrid>

          {/* Graphique principal d'utilisation */}
          <AppUsageChart
            parentsData={parentsData}
            isLoading={isLoadingParents}
            dateRange={dateRange}
            periodFilter={periodFilter}
            showCards={false}
            provenanceFilter={provenanceFilter}
          />
        </Box>

        <Divider my="xl" />

        {/* Section: Activité quotidienne des élèves */}
        <Box>
          <Group spacing="xs" mb="md">
            <ThemeIcon variant="light" color="teal" size="md">
              <School size={18} />
            </ThemeIcon>
            <Title order={3}>Activité quotidienne des élèves</Title>
          </Group>
          <Text size="sm" color="dimmed" mb="lg">
            Activités réalisées par les élèves (diagnostic et aide au devoir)
            sur la période sélectionnée
          </Text>
          <StudentActivityChart
            activityData={studentActivityData}
            isLoading={isLoadingStudentActivity}
            dateRange={dateRange}
            periodFilter={periodFilter}
          />
        </Box>

        <Divider my="xl" />

        {/* Section: Analyse Horaire */}
        <Box>
          <AppUsageByHourChart
            parentsData={parentsData}
            isLoading={isLoadingParents}
            dateRange={dateRange}
            showCards={false}
            provenanceFilter={provenanceFilter}
          />
        </Box>

        <Divider my="xl" />

        {/* Section: Engagement des parents */}
        <Box>
          <Group spacing="xs" mb="md">
            <ThemeIcon variant="light" color="green" size="md">
              <TrendingUp size={18} />
            </ThemeIcon>
            <Title order={3}>Engagement des parents</Title>
          </Group>
          <Text size="sm" color="dimmed" mb="lg">
            Analyse du taux d'engagement et de l'activité des parents sur la
            plateforme (connexions et utilisation de crédits)
          </Text>

          <ParentEngagementChart
            parentsData={parentsData}
            isLoading={isLoadingParents}
            dateRange={dateRange}
            provenanceFilter={provenanceFilter}
          />
        </Box>

        <Divider my="xl" />

        {/* Section: Funnel d'acquisition parents */}
        <Box>
          <Group position="apart" mb={4}>
            <Group spacing="xs">
              <ThemeIcon variant="light" color="blue" size="md">
                <TrendingUp size={18} />
              </ThemeIcon>
              <Title order={3}>Funnel d'acquisition parents</Title>
            </Group>
            <Button
              size="xs"
              variant={funnelByPeriod ? "filled" : "light"}
              color={funnelByPeriod ? "blue" : "gray"}
              leftIcon={<Calendar size={14} />}
              onClick={() => setFunnelByPeriod((v) => !v)}
            >
              {funnelByPeriod
                ? `Période : ${periodFilter === "today" ? "aujourd'hui" : periodFilter === "yesterday" ? "hier" : periodFilter === "last-week" ? "semaine dernière" : periodFilter === "last-month" ? "mois dernier" : periodFilter === "week" ? "cette semaine" : periodFilter === "month" ? "ce mois" : periodFilter === "year" ? "cette année" : periodFilter === "all" ? "tout" : "personnalisé"}`
                : "Tout le temps"}
            </Button>
          </Group>
          <Text size="sm" color="dimmed" mb="lg">
            {funnelByPeriod
              ? "Parents inscrits sur la période sélectionnée."
              : "Tous les parents depuis le début — combien ont atteint chaque étape et quel % se convertit."}
          </Text>

          {(funnelByPeriod ? isLoadingProspectsList : isLoadingFunnelStats) ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <Loader size="lg" />
            </Box>
          ) : funnelByPeriod && errorProspectsList ? (
            <Alert
              icon={<AlertCircle size={16} />}
              title="Erreur"
              color="red"
              mb="md"
            >
              Impossible de charger les données prospects pour le funnel.
            </Alert>
          ) : (
            (() => {
              const totalAll = funnelCumulative[FUNNEL_CONFIG[0].key] || 1;
              return (
                <>
                  {/* ── Graphique funnel horizontal ── */}
                  <Card shadow="sm" p="lg" radius="md" withBorder mb="lg">
                    <Stack spacing={8}>
                      {FUNNEL_CONFIG.map(({ key, label, hex }, index) => {
                        const cumCount = funnelCumulative[key] ?? 0;
                        const count = activeFunnelCounts[key] ?? 0;
                        const pct = (cumCount / totalAll) * 100;
                        const prevCum =
                          index > 0
                            ? (funnelCumulative[FUNNEL_CONFIG[index - 1].key] ??
                              0)
                            : null;
                        const convRate =
                          prevCum && prevCum > 0
                            ? ((cumCount / prevCum) * 100).toFixed(1)
                            : null;
                        const convColor = convRate
                          ? parseFloat(convRate) >= 50
                            ? "#10b981"
                            : parseFloat(convRate) >= 20
                              ? "#f97316"
                              : "#ef4444"
                          : "transparent";

                        return (
                          <Box key={key}>
                            {/* Flèche de conversion entre étapes */}
                            {index > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 4,
                                  paddingLeft: 8,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 1,
                                    background: "#dee2e6",
                                  }}
                                />
                                <Text
                                  size="xs"
                                  weight={700}
                                  sx={{ color: convColor }}
                                >
                                  ↓ {convRate}% se convertissent
                                </Text>
                              </Box>
                            )}

                            {/* Ligne de la barre */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                              }}
                            >
                              {/* Numéro */}
                              <Text
                                size="xs"
                                color="dimmed"
                                weight={700}
                                sx={{ minWidth: 20, textAlign: "right" }}
                              >
                                {index + 1}
                              </Text>

                              {/* Label */}
                              <Text
                                size="sm"
                                weight={600}
                                sx={{ minWidth: 150 }}
                              >
                                {label}
                              </Text>

                              {/* Barre proportionnelle */}
                              <Box
                                sx={{
                                  flex: 1,
                                  background: "#f1f3f5",
                                  borderRadius: 4,
                                  height: 28,
                                  position: "relative",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    height: "100%",
                                    width: `${pct}%`,
                                    background: `linear-gradient(90deg, ${hex}cc, ${hex})`,
                                    borderRadius: 4,
                                    transition: "width 0.8s ease",
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: 10,
                                    minWidth: pct > 5 ? "auto" : 0,
                                  }}
                                >
                                  {pct > 8 && (
                                    <Text
                                      size="xs"
                                      weight={700}
                                      sx={{
                                        color: "#fff",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {cumCount.toLocaleString("fr-FR")}
                                    </Text>
                                  )}
                                </Box>

                                {pct <= 8 && (
                                  <Text
                                    size="xs"
                                    weight={700}
                                    sx={{
                                      position: "absolute",
                                      left: 10,
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      color: "#495057",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {cumCount.toLocaleString("fr-FR")}
                                  </Text>
                                )}
                              </Box>

                              {/* % + count brut à droite */}
                              <Box sx={{ minWidth: 110, textAlign: "right" }}>
                                <Text
                                  size="sm"
                                  weight={800}
                                  sx={{ color: hex }}
                                >
                                  {pct.toFixed(1)}%
                                </Text>
                                <Text size="xs" color="dimmed">
                                  {count.toLocaleString("fr-FR")} inscrits
                                </Text>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>

                    {/* Légende */}
                    <Group spacing="xl" mt="lg" position="center">
                      <Group spacing={6}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            background: "#6366f1",
                          }}
                        />
                        <Text size="xs" color="dimmed">
                          Barre = % ayant atteint cette étape ou plus
                        </Text>
                      </Group>
                      <Group spacing={6}>
                        <Text size="xs" color="dimmed" weight={600}>
                          N inscrits
                        </Text>
                        <Text size="xs" color="dimmed">
                          = parents actuellement à cette étape exacte
                        </Text>
                      </Group>
                    </Group>
                  </Card>

                  {/* ── Funnel horizontal en cards ── */}
                  {(() => {
                    // Calcul des taux étape par étape
                    const rates = FUNNEL_CONFIG.map(({ key }, idx) => {
                      if (idx === 0) return null;
                      const cur = activeFunnelCounts[key] ?? 0;
                      const prev =
                        activeFunnelCounts[FUNNEL_CONFIG[idx - 1].key] ?? 0;
                      return prev > 0 ? (cur / prev) * 100 : null;
                    });
                    // Index de la plus faible conversion
                    let lowestIdx = -1,
                      lowestRate = Infinity;
                    rates.forEach((r, i) => {
                      if (r !== null && r < lowestRate) {
                        lowestRate = r;
                        lowestIdx = i;
                      }
                    });

                    return (
                      <Box mt="xl" sx={{ overflowX: "auto", paddingBottom: 8 }}>
                        <Flex align="flex-start" wrap="nowrap" gap={0}>
                          {FUNNEL_CONFIG.map(({ key, shortLabel }, idx) => {
                            const count = activeFunnelCounts[key] ?? 0;
                            const rate = rates[idx];
                            const isLowest = idx === lowestIdx;
                            const rateColor =
                              rate === null
                                ? "inherit"
                                : rate >= 70
                                  ? "#16a34a"
                                  : rate >= 40
                                    ? "#f97316"
                                    : "#ef4444";

                            return (
                              <Flex
                                key={key}
                                align="center"
                                sx={{ flex: "0 0 auto" }}
                              >
                                {/* Carte — espace badge réservé sur toutes les cartes */}
                                <Box
                                  sx={{ position: "relative", paddingTop: 22 }}
                                >
                                  {isLowest && (
                                    <Badge
                                      color="red"
                                      variant="filled"
                                      size="sm"
                                      radius="sm"
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        whiteSpace: "nowrap",
                                        zIndex: 1,
                                      }}
                                    >
                                      Plus faible conversion
                                    </Badge>
                                  )}
                                  <Box
                                    sx={{
                                      width: 140,
                                      minHeight: 110,
                                      padding: 14,
                                      borderRadius: 8,
                                      border: isLowest
                                        ? "2px solid #ef4444"
                                        : "1px solid #d1fae5",
                                      background: isLowest
                                        ? "#fff5f5"
                                        : "#f0fdf4",
                                      boxSizing: "border-box",
                                    }}
                                  >
                                    <Text
                                      size="sm"
                                      weight={600}
                                      color="dimmed"
                                      mb={4}
                                    >
                                      {shortLabel ?? key}
                                    </Text>
                                    <Text
                                      size={28}
                                      weight={800}
                                      lh={1.1}
                                      mb={rate !== null ? 4 : 0}
                                      color={isLowest ? "red" : "dark"}
                                    >
                                      {count.toLocaleString("fr-FR")}
                                    </Text>
                                    {rate !== null && (
                                      <>
                                        <Text
                                          size="sm"
                                          weight={700}
                                          sx={{ color: rateColor }}
                                          mb={2}
                                        >
                                          {rate.toFixed(1)}%
                                        </Text>
                                        <Text size="xs" color="dimmed">
                                          ↗ 0.0%
                                        </Text>
                                      </>
                                    )}
                                  </Box>
                                </Box>
                                {/* Losange séparateur */}
                                {idx < FUNNEL_CONFIG.length - 1 && (
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      background: "#a7f3d0",
                                      transform: "rotate(45deg)",
                                      borderRadius: 2,
                                      flexShrink: 0,
                                      margin: "0 4px",
                                      marginTop: 0,
                                    }}
                                  />
                                )}
                              </Flex>
                            );
                          })}
                        </Flex>
                      </Box>
                    );
                  })()}
                </>
              );
            })()
          )}
        </Box>

        <Divider my="xl" />

        {/* Section: Répartition par niveau scolaire */}
        <Box>
          <Group spacing="xs" mb="md">
            <ThemeIcon variant="light" color="violet" size="md">
              <ChartPie size={18} />
            </ThemeIcon>
            <Title order={3}>Répartition par niveau scolaire</Title>
          </Group>
          <Text size="sm" color="dimmed" mb="lg">
            Distribution des élèves par classe et niveau sur la période
            sélectionnée
          </Text>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <div style={{ height: "400px", position: "relative" }}>
              {isLoading ? (
                <Box
                  h="100%"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Loader size="lg" />
                </Box>
              ) : (
                <Doughnut data={levelByChartData} options={doughnutOptions} />
              )}
            </div>

            <Text
              align="center"
              color="dimmed"
              size="xs"
              mt="lg"
              transform="uppercase"
              weight={600}
              letterSpacing={0.5}
            >
              Répartition totale des élèves par niveau
            </Text>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
};

export default StatistiquesPage;
